/**
 * PATH: packages/kanban-host/src/kanbanMembersAdapter.ts
 * FILE_ID: KANBANHOST-KANBANMEMBERSADAPTER-001
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/kanbanMembersAdapter.ts
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/kanbanMembersAdapter.ts: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 */

import type { KanbanMember, KanbanMembersAdapter } from '@quotech/ui/kanban'

import { QuotechApiError } from '@quotech/tenant-shared/services/quotechApi'
import type { TeamMember, TeamMembersResponse } from '@quotech/tenant-shared/settings/quotechSettings'


// ─── Types injected by the page (testable seam) ────────────────

export interface BuildMembersAdapterDeps {
  /** API call that returns the raw team-member list for the org.
   *  Page passes `getTeamMembers` from quotechSettings; tests pass
   *  a controllable mock. */
  fetchMembers(orgId: number): Promise<TeamMembersResponse>
  /** Re-runs the session-discovery flow and returns the refreshed
   *  session payload (or null on failure). On a 403 the adapter
   *  calls this then retries the fetch ONCE with the refreshed
   *  org id — the EK-13b stale-cache recovery path. */
  refreshSession(): Promise<{ organisation?: { id?: number } | null } | null>
  /** Optional log sink (defaults to `console.warn`). Tests pass a
   *  vi.fn() so we can assert which branch fired without parsing
   *  console output. */
  warn?: (message: string, context?: Record<string, unknown>) => void
  /** Optional: provision a brand-new "pending onboarding" member by
   *  display name and return the raw team-member row. The page passes
   *  `quickAddTeamMember` from quotechSettings; omit to disable the
   *  `@mention` inline-create affordance. When present, the built
   *  adapter exposes a `createMember` method. */
  createMember?(orgId: number, name: string): Promise<TeamMember>
}


// ─── The pure builder ──────────────────────────────────────────

/**
 * Build a `KanbanMembersAdapter` for the given org id.
 *
 * Returns `null` if `orgId` is undefined — the page passes this
 * straight through so the kanban primitive's `membersAdapter`
 * prop stays optional.
 *
 * Behaviour matrix on `listMembers()`:
 *   200          → render members
 *   403 first    → refreshSession() then ONE retry with fresh org id
 *   403 retried  → []  + warn ("genuinely lost access")
 *   404          → []  + warn ("org not found any more")
 *   anything else→ []  + warn ("transient error / 5xx / 429 / parse")
 */
export function buildMembersAdapter(
  orgId: number | undefined,
  deps: BuildMembersAdapterDeps,
): KanbanMembersAdapter | null {
  if (orgId === undefined) return null
  const warn = deps.warn ?? ((m, c) => {
    // eslint-disable-next-line no-console
    console.warn(m, c)
  })

  async function fetchOnce(targetOrgId: number): Promise<KanbanMember[]> {
    const resp = await deps.fetchMembers(targetOrgId)
    return (resp?.members ?? []).map(toKanbanMember)
  }

  const adapter: KanbanMembersAdapter = {
    listMembers: async (): Promise<KanbanMember[]> => {
      try {
        return await fetchOnce(orgId)
      } catch (err) {
        if (err instanceof QuotechApiError && err.status === 403) {
          // Stale-cache recovery — see file header. Retry exactly
          // once with whatever org the refresh hands back.
          let nextOrgId: number | undefined
          try {
            const refreshed = await deps.refreshSession()
            nextOrgId = refreshed?.organisation?.id
          } catch {
            // refreshSession itself failed — fall through to the
            // generic warn-and-empty branch below.
          }
          if (nextOrgId !== undefined && nextOrgId !== orgId) {
            try {
              return await fetchOnce(nextOrgId)
            } catch {
              // The refreshed session also can't see members —
              // genuinely no access. Don't loop.
            }
          }
          warn('[kanban] members directory: 403 after refresh; user has no access', { orgId })
          return []
        }

        if (err instanceof QuotechApiError && err.status === 404) {
          warn('[kanban] members directory: org not found', { orgId })
          return []
        }

        warn('[kanban] members directory unavailable; rendering with empty list', {
          orgId,
          message: err instanceof Error ? err.message : String(err),
        })
        return []
      }
    },
  }

  // Only expose the inline-create affordance when the page wired a
  // creator — keeps the capability discoverable via duck-typing
  // (`adapter.createMember ? …`) and the email-hub consumer (which
  // passes no creator) unaffected.
  if (deps.createMember) {
    const create = deps.createMember
    adapter.createMember = async (name: string): Promise<KanbanMember> => {
      const row = await create(orgId, name)
      return toKanbanMember(row)
    }
  }

  return adapter
}


// ─── Plain mapper (also exported so the test can pin its shape) ──

export function toKanbanMember(m: TeamMember): KanbanMember {
  return {
    id: m.id,
    name: m.full_name || `${m.first_name} ${m.last_name}`.trim() || m.email,
    email: m.email,
    avatar_url: m.profile_image ?? null,
    role: m.role,
  }
}
